import React, { useContext, useState, useCallback, useEffect } from 'react';
import ExportModal from 'src/components/Modals/ExportModal';
import { ExportCheckBox } from 'src/components/BeaconV2Components/common/AppExportModal/AppExportModal';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { ExportDropdownOptionIds } from 'src/components/BeaconV2Components/common/AppExportModal/exportDataConfigData';
import { getDropdownOptions, SECONDARY_DROPDOWN, ADVERTISING_EXPORT_TYPES } from './utils';
import { ExportContext } from 'src/providers/ExportContextProvider';
import { ExportProps, useBuildExportRequest } from 'src/utils/Hooks/useExportBuilder';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import { GetAdMetricIndexName } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdMetric/AdMetricsUtils';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';

export const AdMetricModal = ({
  open,
  onClose,
  builderProps
}: {
  open: boolean;
  onClose: () => void;
  builderProps: ExportProps;
}) => {
  const [isIncludeComparison, setIsIncludeComparison] = useState(false);
  const [isSeparatedByDay, setIsSeparatedByDay] = useState(false);
  const queryParams = useAppSelector((state) => state.app.queryParams);

  const DROPDOWN_OPTIONS = getDropdownOptions();

  const [selectedGroupBy, setSelectedGroupBy] = useState(DROPDOWN_OPTIONS[0]);
  const [secondaryGroupBy, setSecondaryGroupBy] = useState(SECONDARY_DROPDOWN[selectedGroupBy.id][0]);
  const { startPolling } = useContext(ExportContext);
  const [isExportButtonDisabled, setExportButtonDisabled] = useState(false);
  const { startDayId, endDayId } = useAppSelector((state) => state.comparisonTimePeriod);

  const indexName = GetAdMetricIndexName(selectedGroupBy.entityType, secondaryGroupBy.entityType);
  builderProps.indexName = indexName;

  useEffect(() => {
    setSecondaryGroupBy(SECONDARY_DROPDOWN[selectedGroupBy.id][0]);
  }, [selectedGroupBy]);

  const newAgg = useCallback(
    (agg: AggregationBuilder) => {
      if (isIncludeComparison) {
        agg.addComparisonRangeFilter('dayId', startDayId, endDayId);
      }
      return builderProps.buildAggregation ? builderProps.buildAggregation(agg) : agg;
    },
    [builderProps, endDayId, isIncludeComparison, startDayId]
  );

  const { exportCallback } = useBuildExportRequest({
    ...builderProps,
    // TODO: add custom exportId when we resolve data export service for adGroupName,stacklineSku
    groupBy: selectedGroupBy.id,
    secondaryGroupBy: secondaryGroupBy.id,
    sortFieldName: 'clicks',
    isSeparateDay: isSeparatedByDay,
    buildAggregation: newAgg
  });

  const { showSnackbar } = useSnackbar();

  const handleExportButton = useCallback(() => {
    setExportButtonDisabled(true);
    exportCallback()
      .then(() => {
        startPolling();
        showSnackbar({ type: 'success', message: 'Your download is processing.' });
        onClose();
      })
      .catch((error) => {
        console.error('Export failed:', error);
        showSnackbar({
          message: <GenericErrorMessage />,
          type: 'error'
        });
      })
      .finally(() => {
        setExportButtonDisabled(false);
      });
  }, [exportCallback, onClose, showSnackbar, startPolling]);

  const handleClose = () => {
    setIsIncludeComparison(false);
    setIsSeparatedByDay(false);
    setSelectedGroupBy(DROPDOWN_OPTIONS[0]);
    onClose();
  };

  const handleDropdownChange = (option: { id: ADVERTISING_EXPORT_TYPES; label: string; entityType: string }) => {
    setSelectedGroupBy(option);
  };

  return (
    <ExportModal open={open} onClose={() => {}}>
      <ExportModal.Header title="Export Advertising Metrics" onClose={onClose} />
      <ExportModal.Body
        isIncludeComparison={isIncludeComparison}
        setIsIncludeComparison={setIsIncludeComparison}
        customTimePeriodComponent={
          <>
            <ExportCheckBox
              label="Separate data by day"
              onSelect={(e) => setIsSeparatedByDay(e.target.checked)}
              id="separateDataByDay"
              isSelected={isSeparatedByDay}
              checkboxProps={{
                checked: isSeparatedByDay
              }}
            />
            <ExportCheckBox
              isSelected={isIncludeComparison}
              id="includeComparison"
              label="Include comparison time period"
              onSelect={(e) => setIsIncludeComparison(e.target.checked)}
            />
          </>
        }
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <SlDropdownMenu
            defaultLabel={ExportDropdownOptionIds.PRODUCT}
            options={DROPDOWN_OPTIONS}
            selectedId={selectedGroupBy?.id}
            onChange={(option) => handleDropdownChange(option as any)}
            stacklineButtonProps={{
              buttonSx: {
                minWidth: '99px',
                justifyContent: 'space-between'
              }
            }}
          />
          &
          <SlDropdownMenu
            defaultLabel={ExportDropdownOptionIds.PRODUCT}
            options={SECONDARY_DROPDOWN[selectedGroupBy.id]}
            selectedId={secondaryGroupBy.id}
            onChange={(option) => {
              setSecondaryGroupBy(option as any);
            }}
            stacklineButtonProps={{
              buttonSx: {
                minWidth: '99px',
                justifyContent: 'space-between'
              }
            }}
          />
        </div>
      </ExportModal.Body>
      <ExportModal.Footer
        searchParams={queryParams.searchParams}
        handleClose={handleClose}
        handleExportClick={handleExportButton}
        exportButtonDisabled={isExportButtonDisabled}
      />
    </ExportModal>
  );
};
